/**
 * shared/types/chores.ts — Chores: cadence/recurrence/assign modes, reactions, the fairness surface, ChoreItem and the ChoresAPI slice.
 *
 * Moved verbatim out of the former shared/types.ts monolith; the public
 * surface is unchanged and shared/types/index.ts re-exports it.
 */
import type { RecurrenceRule } from './requests.ts';
import type { FairnessEvaluation } from '../fairness.ts';

/* ---- Chores (card LhzRSTrWTY9w) ---- */

/** 'once' fires on a single date and closes; 'recurring' rolls its due date
 *  forward on every completion so the row returns on the next occurrence. */
export type ChoreCadence = 'once' | 'recurring';
export type ChoreFreq = RecurrenceRule['freq'];

/** How a recurring chore finds its next date (card VmoORelj1HuO).
 *  'fixed'      — the classic rule: daily / weekly / monthly from the due date.
 *  'interval'   — every N days, counted from the schedule, so a late tick
 *                 catches up to the next future slot instead of landing overdue.
 *  'completion' — N days after the tick itself: the rhythm follows the work,
 *                 which is what "every time I've done it, again in 3 days"
 *                 means in a household. */
export type ChoreRecurMode = 'fixed' | 'interval' | 'completion';

/** How a chore decides who is on it (card UeOwZSHfdZCh).
 *  'fixed'     — the row carries its assignee and only a reassignment moves
 *                it: the default, and the only rule chores written before
 *                this card could have had.
 *  'claim'     — nobody until someone takes it. Open at creation whatever the
 *                request sent, claimed by the first tapper, released back to
 *                open by the completion that ends the round.
 *  'alternate' — the pointer walks the couple on completions alone: the
 *                finisher passes the torch to the member who did not finish.
 *                No date, no weekday, no calendar anywhere in the rule. */
export type ChoreAssignMode = 'fixed' | 'claim' | 'alternate';

/** Valid modes, for the two doors that validate a request (DO + worker). */
export const CHORE_ASSIGN_MODES: ChoreAssignMode[] = ['fixed', 'claim', 'alternate'];

/** One day in ms — the unit both non-classic modes count in. */
export const CHORE_DAY = 86_400_000;

/** Sanity ceiling for an interval/completion step (a year), so a fat-fingered
 *  "every 9000 days" is a bad_payload rather than a chore nobody sees again. */
export const CHORE_MAX_EVERY_DAYS = 365;

/** How many times one occurrence may be pushed out before the couple is asked
 *  to change something instead (card xvom08w4BgGT: snooze max 3 per window,
 *  then offer reassign / renegotiate). Snooze and defer both consume it —
 *  they are the same "not this round" gesture with different destinations —
 *  and a completion resets it, because that ends the occurrence. */
export const CHORE_SNOOZE_CAP = 3;

/** What the options card offers when the cap is hit. The client renders
 *  these as verbs; the worker never assumes which one it will get. */
export type ChoreCapOption = 'reassign' | 'renegotiate';

/** Allow-listed reaction glyphs for a chore card (card MnorjNDwB3of). A
 *  closed set — not free text — is what makes comment threads impossible by
 *  design: there is nothing to type, so there is nothing to reply to. */
export const CHORE_REACTION_GLYPHS = ['👏', '❤️', '🙌', '🔥', '😮', '🎉'] as const;
export type ChoreReactionGlyph = (typeof CHORE_REACTION_GLYPHS)[number];

/** Max length of a done-note — a warm receipt line, not a message (card
 *  MnorjNDwB3of). Beyond this the worker answers too_long, matching the
 *  composer's maxLength so the two never disagree. */
export const CHORE_DONE_NOTE_MAX = 140;

/** One member's public reaction on a chore card. At most one per member — a
 *  new glyph replaces theirs, so a card never grows a comment pile. */
export interface ChoreReaction {
  memberId: string;
  glyph: string;
  at: number;
}

export interface ChoreItem {
  choreId: string;
  title: string;
  cadence: ChoreCadence;
  /** Only set when cadence === 'recurring'. */
  freq: ChoreFreq | null;
  /** Which rule drives the roll-forward. Rows written before this column
   *  existed read NULL, which means 'fixed' — the only rule they could have
   *  had. Always 'fixed' while cadence === 'once'. */
  recurMode: ChoreRecurMode;
  /** Step in days for 'interval' and 'completion'; null for 'fixed'. */
  recurEveryDays: number | null;
  /** Member the chore is assigned to; null = anyone's (or, in 'claim' mode,
   *  not yet taken). For 'alternate' it is the pointer: who goes next. */
  assigneeId: string | null;
  /** Which rule governs the assignee (card UeOwZSHfdZCh). A row written
   *  before the column existed reads NULL, which means 'fixed'. */
  assignMode: ChoreAssignMode;
  /** One-off due date, or the recurring row's NEXT occurrence. */
  dueAt: number | null;
  status: 'open' | 'done';
  completedAt: number | null;
  completedBy: string | null;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  /** Chosen at creation (card eudAJs92EZ9B): false means this chore stays in
   *  Pear and is never pushed to Google Calendar / Tasks. There is no edit
   *  path for it — the toggle is part of the create form, so a row that went
   *  out keeps going out and one that stayed in stays in. */
  syncGoogle: boolean;
  /** True when the create form's "Specify time" field was filled: dueAt then
   *  carries a real clock time (a time-block) instead of the local-NOON
   *  date-only sentinel. The sentinel can only be recognised in the client's
   *  own timezone, so the worker never infers it from the timestamp — the
   *  client states it (card ZgLp6PjCXHEm). NULL in the row = written before
   *  the column existed = date-only, which is how those rows always synced. */
  hasTime: boolean;
  /** Times THIS occurrence has been pushed out (snooze or defer). Resets to 0
   *  when the chore completes, which ends the occurrence (card xvom08w4BgGT).
   *  NULL column on a row written before the field existed reads 0. */
  snoozeCount: number;
  /** Public reactions on this card (card MnorjNDwB3of) — at most one entry
   *  per member, so a couple's two taps are the whole thread. Empty when
   *  nobody has reacted. */
  reactions: ChoreReaction[];
  /** The warm receipt left when this occurrence was ticked off — "done — out
   *  of bin liners" (card MnorjNDwB3of). NULL when the completer skipped the
   *  note. For a 'once' row it lives on the closed card; for a recurring row
   *  it is cleared with the round, so the next tick starts note-free. */
  doneNote: string | null;
  /** Who left the done-note — usually the completer, but the route lets the
   *  partner add one to a card they did not finish. NULL with doneNote. */
  doneNoteBy: string | null;
}

/** Step per cadence unit, in ms. Monthly is the same approximation the
 *  request recurrence uses, so one date rolls forward identically everywhere. */
export const CHORE_FREQ_STEPS: Record<ChoreFreq, number> = {
  daily: 86_400_000,
  weekly: 604_800_000,
  monthly: 2_592_000_000,
};

/** Next due date after a completion: the step lands on the CURRENT due date
 *  when one is set, else on the completion clock — an undated recurring chore
 *  starts its cycle at the first tick rather than being born overdue. */
export function nextChoreDueAt(dueAt: number | null, freq: ChoreFreq, now: number): number {
  return (dueAt ?? now) + CHORE_FREQ_STEPS[freq];
}

/** Next due date for a row under whichever rule it carries (card VmoORelj1HuO).
 *  'fixed' keeps the arithmetic it has always had — the probe pins it — so
 *  only the two new modes get their own branch. */
export function nextChoreDueFor(
  chore: {
    dueAt: number | null;
    freq: ChoreFreq | null;
    recurMode?: ChoreRecurMode | null;
    recurEveryDays?: number | null;
  },
  now: number,
): number {
  const mode = chore.recurMode ?? 'fixed';
  if (mode === 'completion') {
    const step = Math.max(1, Math.round(chore.recurEveryDays ?? 1)) * CHORE_DAY;
    return now + step;
  }
  if (mode === 'interval') {
    const step = Math.max(1, Math.round(chore.recurEveryDays ?? 1)) * CHORE_DAY;
    // Counted from the schedule, not from the tick: finishing on day 5 of a
    // 3-day rhythm lands the next one on day 6, and stepping forward from a
    // long-neglected date skips the past rather than booking an overdue row.
    let next = (chore.dueAt ?? now) + step;
    while (next <= now) next += step;
    return next;
  }
  return nextChoreDueAt(chore.dueAt, chore.freq ?? 'weekly', now);
}

/** The couple's list-clear streak (card xvom08w4BgGT). Couple-scoped by
 *  construction — one row per Durable Object, never attributed to a person —
 *  and counted in ISO weeks: a week whose list goes clear counts once, and a
 *  week that passes without a clear run resets the streak to 1 at the next
 *  clear rather than to 0, because the celebration is the clear itself. */
export interface ChoreStreak {
  /** Consecutive weeks the whole list has gone clear. */
  streak: number;
  /** ISO week key (YYYYWW) of the last clear, or null if it never happened. */
  lastClearWeek: number | null;
  /** When that clear fired — the client celebrates once per event, not once
   *  per visit, by remembering the last value it has already shown. */
  lastClearAt: number | null;
}

/** What the fairness engine MAY tell a client (card S1YSeaemeA2N): whether it
 *  wants to speak and on what — never the counts. The per-member reliability
 *  numbers are stripped inside the DO, because a scoreboard between the two
 *  people using the product is ammunition (spec kZX3gsJkKaTH, constraints). */
export interface FairnessPublicState {
  /** Enough history (21 days) and every member past the occurrence floor. */
  eligible: boolean;
  fires: boolean;
  reason: 'lag' | 'reliability' | null;
  /** Why it stayed quiet — history / minimum / alternating / consistent. */
  quiet: 'history' | 'minimum' | 'alternating' | 'consistent' | 'no-chore' | null;
  choreId: string | null;
  laggingMemberId: string | null;
  /** Slice C: the card currently on the rail, or null. Both partners read the
   *  SAME card — there is no private version of the question. */
  card: FairnessCardPublic | null;
}

/** The drift card itself (spec §3): one chore, one move, one pair of answers.
 *  Carries no counts and no reason string — the same scoreboard rule as the
 *  state above, because the card is what a partner actually sees. */
export interface FairnessCardPublic {
  choreId: string;
  /** For the button's own words ("Move 'Vacuum' to Maya"). */
  choreTitle: string;
  /** Whose name the chore currently carries, and who it would move to. */
  fromMemberId: string | null;
  toMemberId: string;
  createdAt: number;
  /** The card self-expires: after this it is gone whether or not anyone read
   *  it, so a question can never sit in the rail for a month. */
  expiresAt: number;
}

/** What the weekly pass reports back (spec §3) — `reason` is why it did NOT
 *  offer a card, and is null exactly when a card was created. Probe-only in
 *  practice: the rail reads the state, not this. */
export interface FairnessPassResult {
  card: FairnessCardPublic | null;
  reason: string | null;
}

/** Dev-only: the full evaluation plus the rows behind it, so the probe can
 *  assert the thresholds rather than infer them. Served only when
 *  PEAR_ENV != prod (worker/src/api/chores.ts). */
export interface FairnessDebugState {
  evaluation: FairnessEvaluation;
  ledger: { choreId: string; memberId: string; completedAt: number; dueAt: number | null; wasLate: boolean }[];
  lag: { choreId: string; firstMissedAt: number; misses: number; assigneeId: string | null; assignMode: string }[];
  /** Ledger rows the backfill just rebuilt from the chores' own doer stamps. */
  backfilled: number;
}

export interface ChoresAPI {
  createChore(input: {
    createdBy: string;
    title: string;
    cadence: ChoreCadence;
    freq?: ChoreFreq | null;
    /** Which roll-forward rule: default 'fixed'. 'interval' and 'completion'
     *  carry recurEveryDays (1–365) instead of a freq. */
    recurMode?: ChoreRecurMode | null;
    recurEveryDays?: number | null;
    /** Which rule owns the assignee (card UeOwZSHfdZCh): default 'fixed'.
     *  'claim' refuses a seeded assignee — a claim chore starts open. */
    assignMode?: ChoreAssignMode | null;
    assigneeId?: string | null;
    dueAt?: number | null;
    /** Default true: the "Add to Calendar & Tasks" switch on the create form
     *  (false keeps the chore out of the Google outbox entirely). */
    syncGoogle?: boolean;
    /** True when the "Specify time" field was filled — dueAt then carries a
     *  clock time instead of the local-NOON date-only sentinel. */
    hasTime?: boolean;
  }): Promise<ChoreItem>;
  /** Open work first, then dated, then done — the order the Life screen draws. */
  listChores(): Promise<ChoreItem[]>;
  /** assigneeId null clears the assignment. An id outside this couple is
   *  Error('bad_payload'); an unknown chore is Error('not_found'). A 'claim'
   *  chore is the exception: it can only be released (null) or claimed by
   *  the caller — handing it to someone else is Error('bad_payload'). */
  assignChore(choreId: string, assigneeId: string | null, actorId?: string | null): Promise<ChoreItem>;
  /** Take an open 'claim' chore (card UeOwZSHfdZCh). Claiming your own is a
   *  no-op, a chore the partner already holds is Error('already_claimed'),
   *  one that is not in 'claim' mode is Error('bad_payload'). */
  claimChore(choreId: string, memberId: string): Promise<ChoreItem>;
  /** 'once' -> status 'done'. 'recurring' -> completed_by stamped and dueAt
   *  rolled forward, status stays 'open'. A finished row is
   *  Error('illegal_transition'). Two rules ride along with the tick: an
   *  'alternate' row whose tick left it open hands the pointer to the member
   *  who did NOT finish, and a 'claim' row whose tick left it open goes back
   *  to unclaimed so the next round can be taken.
   *
   *  The optional note (card MnorjNDwB3of) is the warm receipt — "done — out
   *  of bin liners". Trimmed, capped at CHORE_DONE_NOTE_MAX (over →
   *  Error('too_long')), stored with the member who ticked. A recurring row
   *  clears it with the round; a 'once' row keeps it on the closed card. */
  completeChore(choreId: string, memberId: string, note?: string | null): Promise<ChoreItem>;
  /** Set the caller's public reaction on a chore (card MnorjNDwB3of). The
   *  glyph is one of CHORE_REACTION_GLYPHS (else Error('bad_payload')), one
   *  per member — a second tap with a different glyph replaces theirs, the
   *  same glyph clears it (a toggle). Any member may react to any row, open
   *  or done. An unknown chore is Error('not_found'). No threads by design:
   *  a reaction is a glyph, never a reply. */
  reactChore(choreId: string, memberId: string, glyph: string): Promise<ChoreItem>;
  /** Add or replace a done-note on a row the caller did NOT tick off (card
   *  MnorjNDwB3of) — the partner can still leave a receipt. Empty string
   *  clears it. Over the cap → Error('too_long'); a row that has never been
   *  completed is Error('illegal_transition') — a receipt needs a completion
   *  to receipt. Either member may write it; doneNoteBy records who. */
  setChoreDoneNote(choreId: string, memberId: string, note: string): Promise<ChoreItem>;
  /** Push THIS occurrence to a chosen return time (card xvom08w4BgGT). The
   *  first three pushes in an occurrence land; the fourth is Error('snooze_cap')
   *  — the couple has already moved it three times, so the answer is a change
   *  of plan (reassign / renegotiate), not a fourth date. An unknown chore is
   *  Error('not_found'), a finished one Error('illegal_transition'), a return
   *  time in the past Error('bad_payload'). */
  snoozeChore(choreId: string, memberId: string, returnAt: number): Promise<ChoreItem>;
  /** Move the occurrence to its next natural slot without choosing a date
   *  (same cap, same reset rules as snooze): a recurring row rolls under its
   *  own recurrence rule, a 'once' row is pushed a day. */
  deferChore(choreId: string, memberId: string): Promise<ChoreItem>;
  /** The couple's list-clear streak — read-only, and never keyed to a member. */
  choreStreak(): Promise<ChoreStreak>;
  /** Dev-only probe seam: place the last clear in a past week so the streak's
   *  gap-reset can be tested without waiting a week out. The worker route
   *  refuses it when PEAR_ENV=prod (card xvom08w4BgGT). */
  devStreakSet(lastClearWeek: number, streak: number, now: number): Promise<ChoreStreak>;
  /** Fairness engine, user-facing half (card S1YSeaemeA2N, spec
   *  kZX3gsJkKaTH): evaluate the ledger + lag and say whether the engine
   *  wants to speak. Runs the backfill and the lag sweep on the way through,
   *  and carries no per-member numbers. */
  fairnessState(): Promise<FairnessPublicState>;
  /** Slice C (spec §3): the weekly pass. Creates at most one card, only when
   *  the rule layer fires and both caps allow it. Returns the card, or why
   *  there is none. Never throws. */
  runFairnessCardPass(now: number): Promise<FairnessPassResult>;
  /** Accept the live card: the chore flips to 'alternate' with the pointer on
   *  the taker and the books are archived. No live card is
   *  Error('illegal_transition'). */
  acceptFairnessCard(memberId: string, now: number): Promise<FairnessCardPublic>;
  /** "Not now": the card closes for good (its 45-day quiet was stamped at
   *  offer time, so this chore cannot come back before then). */
  dismissFairnessCard(memberId: string, now: number): Promise<FairnessCardPublic>;
  /** Dev-only probe seam: run the pass with an injected clock, so the caps and
   *  the TTL are testable without waiting them out. Route refuses it at prod. */
  devFairnessPass(now: number): Promise<FairnessPassResult>;
  /** Dev-only probe seam: backdate ledger rows (and optionally prune them so
   *  the backfill has a pre-ledger couple), then return the full evaluation.
   *  The worker route refuses it when PEAR_ENV=prod. */
  devFairness(input: {
    pruneLedger?: boolean;
    entries?: { choreId?: unknown; memberId?: unknown; completedAt?: unknown; dueAt?: unknown; wasLate?: unknown }[];
  }, now: number): Promise<FairnessDebugState>;
  /** Edit what the row carries (card VmoORelj1HuO): title, cadence and its
   *  recurrence, due date, assignee. Omitted fields are left alone; a bad
   *  value is Error('bad_payload'), an unknown chore Error('not_found'). */
  updateChore(choreId: string, patch: {
    title?: string;
    cadence?: ChoreCadence;
    freq?: ChoreFreq | null;
    recurMode?: ChoreRecurMode | null;
    recurEveryDays?: number | null;
    dueAt?: number | null;
    hasTime?: boolean;
    assigneeId?: string | null;
    /** Switching a chore INTO 'claim' clears whoever held it — the mode
     *  starts open, so an edit can never leave a claim already filled. */
    assignMode?: ChoreAssignMode | null;
  }): Promise<ChoreItem>;
  /** Remove the row. An unknown chore is Error('not_found'). */
  deleteChore(choreId: string): Promise<void>;
}
